import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Button, notification, Spin, Divider } from 'antd';

import useAuth from '@hooks/use-auth';

import styles from './index.module.scss';

const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

const base64URLEncode = (buffer: Uint8Array) => {
  const base64 = btoa(String.fromCharCode(...Array.from(buffer)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const sha256 = async (plain: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash);
};

const generateCodeChallenge = async (verifier: string) => {
  const hashed = await sha256(verifier);
  return base64URLEncode(hashed);
};

const LoginWithGooglePage: React.FC = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const { login } = useAuth();

  const handleMicrosoftLogin = async () => {
    try {
      const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
      const tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID;
      const redirectUri = `${window.location.origin}/auth/callback`;

      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem('microsoft_oauth_state', state);

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      sessionStorage.setItem('microsoft_code_verifier', codeVerifier);

      const params = new URLSearchParams({
        client_id: clientId || '',
        response_type: 'code',
        redirect_uri: redirectUri,
        response_mode: 'query',
        scope: 'openid profile email User.Read',
        state: state,
        prompt: 'select_account',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });

      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Microsoft login error:', error);
      notification.error({
        message: 'Microsoft Authentication failed',
        description: error.message || 'Please try again',
      });
    }
  };

  return (
    <div className={styles.container}>
      <Spin spinning={isLoading}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <GoogleLogin
            size={'large'}
            onSuccess={async res => {
              setLoading(true);
              await login(res.credential || '', 'google');
              setLoading(false);
            }}
            onError={() => {
              setLoading(false);
              notification.error({ message: 'Google Authentication failed' });
            }}
          />

          <Divider plain>or</Divider>

          <Button
            type="default"
            size="large"
            block
            onClick={handleMicrosoftLogin}
            icon={
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px' }}
              >
                <rect width="10" height="10" fill="#F25022" />
                <rect x="11" width="10" height="10" fill="#7FBA00" />
                <rect y="11" width="10" height="10" fill="#00A4EF" />
                <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
              </svg>
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Sign in with Microsoft
          </Button>
        </div>
      </Spin>
    </div>
  );
};

export default LoginWithGooglePage;
